/**
 * Supabase Realtime Subscriptions
 *
 * リアルタイムでデータベースの変更を監視し、
 * 他のユーザーの更新を即座に反映する
 */

import { getSupabaseClient } from '@/lib/supabase/supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'
import type { Customer, PlanRequest, Contract } from '@/lib/types/database'
import { safeRealtimeSubscribe, safeRealtimeCleanup } from '@/lib/realtime/safe-realtime'

// ============================================
// Types
// ============================================
export type ChangeEventType = 'INSERT' | 'UPDATE' | 'DELETE'

export interface ChangePayload<T> {
  eventType: ChangeEventType
  new: T | null
  old: Partial<T> | null
}

export type ChangeHandler<T> = (payload: ChangePayload<T>) => void

// ============================================
// RealtimeManager
// ============================================
export class RealtimeManager {
  private channels: Map<string, RealtimeChannel> = new Map()
  private _supabase: ReturnType<typeof getSupabaseClient> | null = null

  private get supabase(): ReturnType<typeof getSupabaseClient> {
    if (!this._supabase) {
      this._supabase = getSupabaseClient()
    }
    return this._supabase
  }

  /**
   * 顧客テーブルの変更を監視
   */
  subscribeToCustomers(handler: ChangeHandler<Customer>): () => void {
    return this.subscribe('customers', 'customers-realtime', handler)
  }

  /**
   * 特定のパイプラインステータスの顧客を監視
   */
  subscribeToCustomersByStatus(status: string, handler: ChangeHandler<Customer>): () => void {
    const channelName = `customers-status-${status}`
    return this.subscribe('customers', channelName, handler, `pipeline_status=eq.${status}`)
  }

  /**
   * プラン依頼テーブルの変更を監視
   */
  subscribeToPlanRequests(handler: ChangeHandler<PlanRequest>): () => void {
    return this.subscribe('plan_requests', 'plan-requests-realtime', handler)
  }

  /**
   * 契約テーブルの変更を監視
   */
  subscribeToContracts(handler: ChangeHandler<Contract>): () => void {
    return this.subscribe('contracts', 'contracts-realtime', handler)
  }

  /**
   * 汎用サブスクリプション
   */
  private subscribe<T>(
    table: string,
    channelName: string,
    handler: ChangeHandler<T>,
    filter?: string
  ): () => void {
    if (this.channels.has(channelName)) {
      return () => this.unsubscribe(channelName)
    }

    const channelConfig: {
      event: '*'
      schema: 'public'
      table: string
      filter?: string
    } = {
      event: '*',
      schema: 'public',
      table,
    }

    if (filter) {
      channelConfig.filter = filter
    }

    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', channelConfig, (payload) => {
        handler({
          eventType: payload.eventType as ChangeEventType,
          new: payload.new as T | null,
          old: payload.old as Partial<T> | null,
        })
      })

    // 常時接続を開けないとき subscribe() は同期例外を投げる（WebKit/iPad）。
    // ここは realtimeManager 経由の全購読が通る急所なので、ここで受け止める。
    const started = safeRealtimeSubscribe(`manager:${channelName}`, () => channel.subscribe())

    if (!started) {
      // 失敗したチャンネルを登録すると isConnected() が「接続済み」と嘘をつき、
      // 同名チャンネルの再購読も上の early return で塞がれて再試行できなくなる
      // （Codex SO MEDIUM・2026-08-09）。登録せず、張りかけのチャンネルは破棄する。
      safeRealtimeCleanup(`manager:${channelName}`, () => this.supabase.removeChannel(channel))
      return () => {}
    }

    this.channels.set(channelName, channel)

    return () => this.unsubscribe(channelName)
  }

  /**
   * チャンネルの購読解除
   */
  private unsubscribe(channelName: string): void {
    const channel = this.channels.get(channelName)
    if (channel) {
      // 解除できなかったチャンネルの参照は捨てない（捨てると再解除できずリークを隠す）
      const removed = safeRealtimeCleanup(`manager:${channelName}`, () =>
        this.supabase.removeChannel(channel)
      )
      if (removed) this.channels.delete(channelName)
    }
  }

  /**
   * すべてのチャンネルの購読解除
   */
  unsubscribeAll(): void {
    // 1件の解除失敗で残りの解除を止めない。ただし失敗した分は参照を残す
    // （clear() で一括削除すると、解除できていないチャンネルのリークを隠す）
    for (const [channelName, channel] of [...this.channels]) {
      const removed = safeRealtimeCleanup(`manager:${channelName}`, () =>
        this.supabase.removeChannel(channel)
      )
      if (removed) this.channels.delete(channelName)
    }
  }

  /**
   * 接続状態を確認
   */
  isConnected(channelName: string): boolean {
    return this.channels.has(channelName)
  }

  /**
   * アクティブなチャンネル数を取得
   */
  getActiveChannelCount(): number {
    return this.channels.size
  }
}

// シングルトンインスタンス
export const realtimeManager = new RealtimeManager()
